import { Checkbox, Field, Input, SegmentedControl } from '@/components/ui';

export interface AbSettings {
  abEnabled: boolean;
  abSamplePercent: number;
  abMetric: 'opens' | 'clicks';
  abWaitHours: number;
}

export interface AbTestSectionProps {
  value: AbSettings;
  onChange(patch: Partial<AbSettings>): void;
  disabled?: boolean;
  errors?: Partial<Record<keyof AbSettings, string>>;
}

const METRIC_OPTIONS = [
  { value: 'opens', label: 'Opens' },
  { value: 'clicks', label: 'Clicks' },
] as const;

const AB_SAMPLE_MIN = 10;
const AB_SAMPLE_MAX = 50;
const AB_WAIT_MIN = 1;
const AB_WAIT_MAX = 72;

function parseIntOr(raw: string, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

/**
 * A/B subject testing on a broadcast. The sample is split evenly between A and
 * B; the rest waits for the winner.
 */
export function AbTestSection({ value, onChange, disabled, errors = {} }: AbTestSectionProps) {
  const holdout = 100 - value.abSamplePercent;
  return (
    <div className="grid min-w-0 gap-4">
      <Checkbox
        label="Test two subject lines"
        checked={value.abEnabled}
        disabled={disabled}
        onChange={(e) => onChange({ abEnabled: e.target.checked })}
      />
      {errors.abEnabled && (
        <p role="alert" className="m-0 text-xs text-danger">
          {errors.abEnabled}
        </p>
      )}
      {value.abEnabled && (
        <div className="grid min-w-0 gap-4 sm:grid-cols-3">
          <Field label="Test sample (%)" error={errors.abSamplePercent}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="number"
                inputMode="numeric"
                min={AB_SAMPLE_MIN}
                max={AB_SAMPLE_MAX}
                step={1}
                value={value.abSamplePercent}
                disabled={disabled}
                invalid={Boolean(errors.abSamplePercent)}
                onChange={(e) => onChange({ abSamplePercent: parseIntOr(e.target.value, value.abSamplePercent) })}
                onBlur={() =>
                  onChange({ abSamplePercent: clamp(value.abSamplePercent, AB_SAMPLE_MIN, AB_SAMPLE_MAX) })
                }
              />
            )}
          </Field>
          <div className="grid gap-1.5">
            <span aria-hidden="true" className="text-xs font-medium text-fg-secondary">
              Winner by
            </span>
            <SegmentedControl<'opens' | 'clicks'>
              label="Winner by"
              value={value.abMetric}
              options={METRIC_OPTIONS}
              onChange={(abMetric) => onChange({ abMetric })}
              disabled={disabled}
            />
            {errors.abMetric && <p className="m-0 text-xs text-danger">{errors.abMetric}</p>}
          </div>
          <Field label="Wait before choosing (hours)" error={errors.abWaitHours}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="number"
                inputMode="numeric"
                min={AB_WAIT_MIN}
                max={AB_WAIT_MAX}
                step={1}
                value={value.abWaitHours}
                disabled={disabled}
                invalid={Boolean(errors.abWaitHours)}
                onChange={(e) => onChange({ abWaitHours: parseIntOr(e.target.value, value.abWaitHours) })}
                onBlur={() => onChange({ abWaitHours: clamp(value.abWaitHours, AB_WAIT_MIN, AB_WAIT_MAX) })}
              />
            )}
          </Field>
          <p className="m-0 text-xs text-fg-muted sm:col-span-3">
            {value.abSamplePercent / 2}% get A, {value.abSamplePercent / 2}% get B; the other {Math.max(holdout, 0)}%
            get the winner after {value.abWaitHours} {value.abWaitHours === 1 ? 'hour' : 'hours'}.
          </p>
        </div>
      )}
    </div>
  );
}
